import { type FC, type ReactNode } from "react";

import { Card, Flex, Skeleton, UnstyledButton } from "@mantine/core";

import { myLensesActionType } from "../../analytics/event-creators/createActionEvent";
import { useBlizzardEvents } from "../../analytics/hooks";
import { type GoogleProtobufTimestamp } from "../../gql-generated/types";
import { useTimeRange } from "../../lens-insights/hooks";
import { GranularityInDays, LensGranularity } from "../../lens-insights/models";
import { StatsCard } from "../../shared/components";
import { useToggleState } from "../../shared/hooks";
import { usePayoutDriverStats } from "../hooks/usePayoutDriverStats";

import { HowPayoutsWorkModal } from "./HowPayoutsWorkModal";
import {
    card,
    header,
    headerIcon,
    headerLeft,
    headerSubtitle,
    headerTitle,
    howPayoutsButton,
    skeletonRow,
    skeletonStack,
    statsRow,
} from "./PayoutDriversCard.module.css";
import {
    HowPayoutsWork,
    LensPlusScore,
    LensPlusScoreTooltip,
    LensPlusSubscriptionsEarned,
    LensPlusSubscriptionsEarnedTooltip,
    PayoutDriversHeader,
    PayoutDriversStatsVsPrevious,
    PayoutDriversSubtitle,
    TotalLensPlusAudience,
    TotalLensPlusAudienceTooltip,
    TotalPayoutsLabel,
    TotalPayoutsTooltip,
} from "./messages.generated";

interface PayoutDriversCardProps {
    unlockableId?: string;
    timeRange?: LensGranularity;
    markedAsExclusiveAt?: GoogleProtobufTimestamp | null;
    isResolvingSelectedLens?: boolean;
}

const CARD_COUNT = 4;

const renderHeader = (button: ReactNode): ReactNode => (
    <div className={header}>
        <div className={headerLeft}>
            <span className={headerIcon} aria-hidden="true">
                $
            </span>
            <div>
                <div className={headerTitle}>
                    <PayoutDriversHeader />
                </div>
                <div className={headerSubtitle}>
                    <PayoutDriversSubtitle />
                </div>
            </div>
        </div>
        {button}
    </div>
);

export const PayoutDriversCard: FC<PayoutDriversCardProps> = ({
    unlockableId,
    timeRange: explicitTimeRange,
    markedAsExclusiveAt,
    isResolvingSelectedLens = false,
}) => {
    const { selectedTimeRange } = useTimeRange({ defaultRange: LensGranularity.Lifetime });
    const timeRange = explicitTimeRange ?? selectedTimeRange;
    const days = GranularityInDays[timeRange];

    const stats = usePayoutDriverStats({
        unlockableId,
        timeRange: explicitTimeRange,
        markedAsExclusiveAt,
        skip: isResolvingSelectedLens,
    });
    const [modalProps, showModal] = useToggleState();
    const { sendBlizzardEvent } = useBlizzardEvents();

    const diffText = days ? <PayoutDriversStatsVsPrevious days={days.toString()} /> : null;

    const handleHowPayoutsClick = (): void => {
        sendBlizzardEvent({
            event: "MYLENSES_ACTION",
            actionType: myLensesActionType.click,
            label: "payout_drivers_how_payouts_click",
            namespace: "lenses",
        });
        showModal();
    };

    const howEarningsWorkButton = (
        <UnstyledButton type="button" className={howPayoutsButton} onClick={handleHowPayoutsClick}>
            <HowPayoutsWork />
            <span aria-hidden="true"> →</span>
        </UnstyledButton>
    );

    const isLoading = stats.loading || isResolvingSelectedLens;

    return (
        <>
            <Card shadow="sm" radius={0} p={0} className={card}>
                {renderHeader(howEarningsWorkButton)}
                {isLoading ? (
                    <div className={skeletonRow}>
                        {Array.from({ length: CARD_COUNT }, (_, i) => (
                            <div key={`payout-driver-skeleton-${i}`} className={skeletonStack}>
                                <Skeleton h={14} w="60%" />
                                <Skeleton h={28} w="40%" />
                            </div>
                        ))}
                    </div>
                ) : (
                    <Flex className={statsRow}>
                        <StatsCard
                            label={<TotalLensPlusAudience />}
                            tooltip={<TotalLensPlusAudienceTooltip />}
                            value={stats.audience.value}
                            diffValue={stats.audience.diffValue}
                            diffText={diffText}
                            size="hero"
                        />
                        <StatsCard
                            label={<LensPlusSubscriptionsEarned />}
                            tooltip={<LensPlusSubscriptionsEarnedTooltip />}
                            value={stats.subscriptionsEarned.value}
                            diffValue={stats.subscriptionsEarned.diffValue}
                            diffText={diffText}
                            size="hero"
                        />
                        <StatsCard
                            label={<LensPlusScore />}
                            tooltip={<LensPlusScoreTooltip />}
                            value={stats.score.value}
                            diffValue={stats.score.diffValue}
                            diffText={diffText}
                            size="hero"
                        />
                        <StatsCard
                            label={<TotalPayoutsLabel />}
                            tooltip={<TotalPayoutsTooltip />}
                            value={stats.payout.value}
                            diffValue={stats.payout.diffValue}
                            diffText={diffText}
                            size="hero"
                        />
                    </Flex>
                )}
            </Card>
            <HowPayoutsWorkModal modalProps={modalProps} />
        </>
    );
};
